import { ComponentProps, ReactNode, useCallback } from 'react';
import React from 'react';

import cx from 'classnames';
import { ToggleLayer } from 'react-laag';

import { IRoom } from 'components/shared/Assets/shared/Form/Document/types';
import type { Select } from 'components/shared/Select/adapters/FinalForm';
import { MultiValueContainer as DefaultMultiValueContainer } from 'components/shared/Select/components/Sortable/MultiValueContainer';
import { MultiValueLabel as DefaultMultiValueLabel } from 'components/shared/Select/components/Sortable/MultiValueLabel';
import { isDefined } from 'utils/ramda';

import { RoomsDropdown } from './Dropdown';
import s from './index.module.css';

type Props = {
  children: (renderProps: Partial<ComponentProps<typeof Select>>) => ReactNode;
  value: { [id: string]: Partial<IRoom>[] };
  onChange: (id: number, rooms: Partial<IRoom>[] | null) => void;
};

export function RoomsWrapper(props: Props) {
  const { children, value, onChange } = props;

  const [page, setPage] = React.useState<{ id: number; type: string; editedInProposal: boolean } | null>(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);

  const isOpen = page !== null;

  const MultiValueContainer: typeof DefaultMultiValueContainer = useCallback(
    (mvcProps) => (
      <DefaultMultiValueContainer
        {...mvcProps}
        innerProps={{
          ...mvcProps.innerProps,
          className: cx(mvcProps.innerProps.className, s.multiValue, {
            [s.highlighted]: mvcProps.data.value === page?.id,
          }),
        }}
      />
    ),
    [page],
  );
  const MultiValueLabel: typeof DefaultMultiValueLabel = useCallback((mvlProps) => {
    const option = (
      mvlProps.selectProps.value as { value: number; type: string; editedInProposal: boolean }[] | undefined
    )?.find((option) => option.value === mvlProps.data.value);

    return (
      <div
        onKeyDown={(e) => {
          if (e.key === 'Enter' && option)
            setTimeout(() =>
              setPage({ id: option.value, type: option.type, editedInProposal: option.editedInProposal }),
            );
        }}
        onPointerDown={() => {
          if (option)
            setTimeout(() =>
              setPage({ id: option.value, type: option.type, editedInProposal: option.editedInProposal }),
            );
        }}
        tabIndex={0}
      >
        <DefaultMultiValueLabel {...mvlProps} />
      </div>
    );
  }, []);

  const selectRef = React.useRef(null);

  return (
    <ToggleLayer
      fixed
      isOpen={isOpen}
      onOutsideClick={() => !previewOpen && setPage(null)}
      placement={{
        anchor: 'BOTTOM_LEFT',
        autoAdjust: true,
        triggerOffset: 10,
      }}
      renderLayer={({ isOpen, layerProps }) =>
        isOpen && (
          <div ref={layerProps.ref} className="layer" style={{ ...layerProps.style, zIndex: 1000 }}>
            <RoomsDropdown
              id={page!.id}
              type={page!.type}
              editedInProposal={page!.editedInProposal}
              onDismiss={() => setPage(null)}
              value={value[page!.id]}
              onChange={(rooms) => onChange(page!.id, [...rooms])}
              setPreviewOpen={setPreviewOpen}
              previewOpen={previewOpen}
              selectRef={selectRef}
            />
          </div>
        )
      }
    >
      {({ triggerRef }) => {
        return (
          <div ref={triggerRef}>
            <div ref={selectRef}>
              {children({
                onChange: (e, a) => {
                  if (a?.action === 'select-option' && a.option) {
                    const option = a.option as { value: number; type: string; editedInProposal: boolean };
                    if (option.type === 'property')
                      setPage({ id: option.value, type: option.type, editedInProposal: option.editedInProposal });
                  }
                  if (a?.action === 'remove-value' && a.removedValue)
                    onChange((a.removedValue as { value: number }).value, null);
                },
                components: { MultiValueContainer, MultiValueLabel },
                closeMenuOnSelect: true,
                menuIsOpen: page !== null ? false : undefined,
                // @ts-expect-error
                formatOptionLabel: (option: { label: string; value: string }) => {
                  const handledRoomsLabel = isDefined(value?.[option.value])
                    ? ` (${value[option.value].map((room) => room.name).join(', ')})`
                    : '';

                  return `${option.label}${handledRoomsLabel}`;
                },
              })}
            </div>
          </div>
        );
      }}
    </ToggleLayer>
  );
}
